import { ApolloError } from '@apollo/client'
import { Formik, FormikHelpers, getIn } from 'formik'
import { isNumber } from 'lodash'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { InputNumber } from 'components/InputNumber'
import { Label } from 'components/v2/Label'
import { Row } from 'components/v2/Row'
import { Select } from 'components/v2/Select'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { SelectState } from 'features/Cities/components/SelectState'
import { INewOperationalCostsFormValues } from 'features/OperationalCosts/types'
import { SelectVehicleType } from 'features/Vehicles/components/SelectVehicleType'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'

const DURATION_OPTIONS_IN_HOURS = [
  { start: 0, end: 4, factor: 0 },
  { start: 5, end: 8, factor: 1 },
  { start: 9, end: 12, factor: 1.5 },
  { start: 13, end: 16, factor: 3 },
  { start: 17, end: 24, factor: 4 }
]

const messages = defineMessages({
  businessClientPlaceholder: {
    id: 'newOperationalCosts.form.businessClient.placeholder',
    defaultMessage: 'Select business client'
  },
  businessSitePlaceholder: {
    id: 'newOperationalCosts.form.businessSite.placeholder',
    defaultMessage: 'Select business site'
  },
  baseDistancePlaceholder: {
    id: 'newOperationalCosts.form.baseDistance.placeholder',
    defaultMessage: 'Base Distance'
  },
  vehicleCapacityPlaceholder: {
    id: 'newOperationalCosts.form.capacity.placeholder',
    defaultMessage: 'Enter capacity'
  }
})

interface INewOperationalCostsProps {
  onSubmit: (
    values: INewOperationalCostsFormValues,
    helpers: FormikHelpers<INewOperationalCostsFormValues>
  ) => Promise<void>
  error?: Error | ApolloError | null
}

export const NewOperationalCosts = ({ onSubmit, error }: INewOperationalCostsProps): ReactElement => {
  const intl = useIntl()

  const initialValues: INewOperationalCostsFormValues = {
    stateCode: '',
    vehicleType: null,
    vehicleCapacity: null,
    businessClient: null,
    businessSite: null,
    baseDistance: 0,
    perKm: 1,
    redZone: 1,
    airConditioning: 1,
    durationCost: 0,
    costPerKm: 0,
    costPerDriver: 0,
    fixedCost: 0,
    durationSettings: DURATION_OPTIONS_IN_HOURS.map((durationSetting) => ({
      range: {
        start: durationSetting.start,
        end: durationSetting.end
      },
      factor: durationSetting.factor
    }))
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {({
        handleSubmit,
        dirty,
        values,
        handleBlur,
        handleChange,
        setValues,
        touched,
        errors,
        setFieldValue,
        isSubmitting
      }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <FormLayout.Card small>
              <Heading.H4>
                <FormattedMessage
                  id="operationalCosts.operationalCosts.form.title"
                  defaultMessage="General Information"
                />
              </Heading.H4>
              <Row>
                <Column width="50%">
                  <SelectState
                    id="stateCode"
                    value={values.stateCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched.stateCode}
                    errors={errors.stateCode}
                    label={<FormattedMessage id="dictionary.state" defaultMessage="State" />}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <SelectVehicleType
                    errors={errors.vehicleType}
                    touched={touched.vehicleType}
                    value={values.vehicleType || ''}
                    onChange={(value): void => {
                      setFieldValue('vehicleType', value || null)
                    }}
                  />
                </Column>
                <Column>
                  <Label>
                    <FormattedMessage
                      id="operationalCosts.form.baseCost.vehicleCapacity.label"
                      defaultMessage="Capacity"
                    />
                  </Label>
                  <InputNumber
                    id="vehicleCapacity"
                    onChange={(value: string): void => {
                      setFieldValue('vehicleCapacity', value ? parseInt(value) : null)
                    }}
                    placeholder={intl.formatMessage(messages.vehicleCapacityPlaceholder)}
                    value={isNumber(values.vehicleCapacity) ? values.vehicleCapacity : ''}
                    touched={touched.vehicleCapacity}
                    errors={errors.vehicleCapacity}
                    maskOptions={{ suffix: ' Se\\ats' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <BusinessClientAutocomplete
                    id="businessClient"
                    value={values.businessClient?.name || ''}
                    onChange={(businessClient): void => {
                      setValues({
                        ...values,
                        businessClient: businessClient?.id ? businessClient : null,
                        businessSite: null
                      })
                    }}
                    placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
                    label={
                      <FormattedMessage
                        id="operationalCosts.operationalCosts.form.businessClient.label"
                        defaultMessage="Business Client"
                      />
                    }
                    optional
                  />
                </Column>
                <Column>
                  <BusinessSiteAutocomplete
                    id="businessSite"
                    label={
                      <FormattedMessage
                        id="operationalCosts.operationalCosts.form.businessSite.label"
                        defaultMessage="Business Site"
                      />
                    }
                    placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
                    value={values.businessSite?.name || ''}
                    onChange={(businessSite): void => {
                      setValues({
                        ...values,
                        businessSite: businessSite?.id ? businessSite : null
                      })
                    }}
                    businessClientId={values.businessClient?.id}
                    disabled={!values.businessClient?.id}
                    optional
                  />
                </Column>
              </Row>
            </FormLayout.Card>
            <FormLayout.Card small>
              <Heading.H4>
                <FormattedMessage
                  id="operationalCosts.operationalCosts.form.settings.title"
                  defaultMessage="Settings"
                />
              </Heading.H4>
              <Row>
                <Column width="50%">
                  <Label>
                    <FormattedMessage
                      id="operationalCosts.form.baseCost.label"
                      defaultMessage="AC (Air Conditioning)"
                    />
                  </Label>
                  <InputNumber
                    placeholder="0 x"
                    onChange={(value: string): void => {
                      setFieldValue('airConditioning', value ? parseFloat(value) : null)
                    }}
                    id="airConditioning"
                    value={isNumber(values.airConditioning) ? values.airConditioning : ''}
                    touched={touched.airConditioning}
                    errors={errors.airConditioning}
                    maskOptions={{ allowDecimal: true, suffix: ' x' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column width="50%">
                  <Label>
                    <FormattedMessage id="operationalCosts.form.baseCost.label" defaultMessage="Base Distance" />
                  </Label>
                  <InputNumber
                    placeholder="0 km"
                    onChange={(value: string): void => {
                      setFieldValue('baseDistance', value ? parseFloat(value) : null)
                    }}
                    id="baseDistance"
                    value={isNumber(values.baseDistance) ? values.baseDistance : ''}
                    touched={touched.baseDistance}
                    errors={errors.baseDistance}
                    maskOptions={{ allowDecimal: true, suffix: ' km' }}
                  />
                </Column>
                <Column width="50%">
                  <Label>
                    <FormattedMessage id="operationalCosts.form.baseCost.label" defaultMessage="Km Factor" />
                  </Label>
                  <InputNumber
                    placeholder="0 x"
                    onChange={(value: string): void => {
                      setFieldValue('perKm', value ? parseFloat(value) : null)
                    }}
                    id="perKm"
                    value={isNumber(values.perKm) ? values.perKm : ''}
                    touched={touched.perKm}
                    errors={errors.perKm}
                    maskOptions={{ allowDecimal: true, suffix: ' x' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column width="50%">
                  <Label>
                    <FormattedMessage id="operationalCosts.form.baseCost.label" defaultMessage="Red Zone" />
                  </Label>
                  <InputNumber
                    placeholder="0 x"
                    onChange={(value: string): void => {
                      setFieldValue('redZone', value ? parseFloat(value) : null)
                    }}
                    id="redZone"
                    value={isNumber(values.redZone) ? values.redZone : ''}
                    touched={touched.redZone}
                    errors={errors.redZone}
                    maskOptions={{ allowDecimal: true, suffix: ' x' }}
                  />
                </Column>
              </Row>
              {values.durationSettings.map((_, index): ReactElement => {
                return (
                  <Row key={index}>
                    <Column>
                      <Label>
                        <FormattedMessage
                          id="operationalCosts.form.durationSettings.duration"
                          defaultMessage="Duration"
                        />
                      </Label>
                      <Select
                        value={
                          isNumber(values.durationSettings[index].range?.start)
                            ? JSON.stringify(values.durationSettings[index].range)
                            : ''
                        }
                        onChange={(): void => {
                          // Do nothing
                        }}
                        disabled
                      >
                        {DURATION_OPTIONS_IN_HOURS.map((option, i) => (
                          <option
                            key={i}
                            value={JSON.stringify({
                              start: option.start,
                              end: option.end
                            })}
                          >
                            {`${option.start} - ${option.end} hrs`}
                          </option>
                        ))}
                      </Select>
                    </Column>
                    <Column>
                      <Label>
                        <FormattedMessage
                          id="operationalCosts.form.durationSettings.durationFactor"
                          defaultMessage="Duration Factor"
                        />
                      </Label>
                      <InputNumber
                        id={`durationSettings.${index}.factor`}
                        onChange={(value: string): void => {
                          setFieldValue(`durationSettings.${index}.factor`, value ? parseFloat(value) : null)
                        }}
                        placeholder="0 x"
                        maskOptions={{ allowDecimal: true, suffix: ' x' }}
                        value={
                          isNumber(values.durationSettings[index].factor) ? values.durationSettings[index].factor : ''
                        }
                        touched={getIn(touched, `durationSettings.${index}.factor`)}
                        errors={getIn(errors, `durationSettings.${index}.factor`)}
                      />
                    </Column>
                  </Row>
                )
              })}
            </FormLayout.Card>
            <FormLayout.Card>
              <Heading.H4>
                <FormattedMessage id="operationalCosts.form.cost.title" defaultMessage="Cost" />
              </Heading.H4>
              <Row>
                <Column>
                  <Label>
                    <FormattedMessage id="operationalCosts.form.durationCost.label" defaultMessage="Duration Cost" />
                  </Label>
                  <InputNumber
                    placeholder="$0.00"
                    onChange={(value: string): void => {
                      setFieldValue('durationCost', value ? parseFloat(value) : null)
                    }}
                    id="durationCost"
                    value={isNumber(values.durationCost) ? values.durationCost : ''}
                    touched={touched.durationCost}
                    errors={errors.durationCost}
                    maskOptions={{ allowDecimal: true, prefix: '$' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <Label>
                    <FormattedMessage id="operationalCosts.form.costPerKm.label" defaultMessage="Cost per KM" />
                  </Label>
                  <InputNumber
                    placeholder="$0.00"
                    onChange={(value: string): void => {
                      setFieldValue('costPerKm', value ? parseFloat(value) : null)
                    }}
                    id="costPerKm"
                    value={isNumber(values.costPerKm) ? values.costPerKm : ''}
                    touched={touched.costPerKm}
                    errors={errors.costPerKm}
                    maskOptions={{ allowDecimal: true, prefix: '$' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <Label>
                    <FormattedMessage id="operationalCosts.form.costPerDriver.label" defaultMessage="Cost per Driver" />
                  </Label>
                  <InputNumber
                    placeholder="$0.00"
                    onChange={(value: string): void => {
                      setFieldValue('costPerDriver', value ? parseFloat(value) : null)
                    }}
                    id="costPerDriver"
                    value={isNumber(values.costPerDriver) ? values.costPerDriver : ''}
                    touched={touched.costPerDriver}
                    errors={errors.costPerDriver}
                    maskOptions={{ allowDecimal: true, prefix: '$' }}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <Label>
                    <FormattedMessage id="operationalCosts.form.cost.label" defaultMessage="Cost" />
                  </Label>
                  <InputNumber
                    placeholder="$0.00"
                    onChange={(value: string): void => {
                      setFieldValue('fixedCost', value ? parseFloat(value) : null)
                    }}
                    id="fixedCost"
                    value={isNumber(values.fixedCost) ? values.fixedCost : ''}
                    touched={touched.fixedCost}
                    errors={errors.fixedCost}
                    maskOptions={{ allowDecimal: true, prefix: '$' }}
                  />
                </Column>
              </Row>
            </FormLayout.Card>
            {error && !isSubmitting && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(error as ApolloError)}>
                  <FormattedMessage
                    id="operationalCosts.error"
                    defaultMessage="There was an error while creating your operational cost settings."
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="submit" kind="primary" disabled={!dirty} loading={isSubmitting}>
                <FormattedMessage id="dictionary.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
